import { FC, useEffect, useMemo } from 'react';
import { Card, Col, Image, Row, Select, Space, Tabs, Typography } from 'antd';
import { PhoneOutlined, MessageOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import placeholder from 'assets/img/placeholder.png';
import { LANGUAGES } from 'constants/profile';
import { useParams, useSearchParams } from 'react-router-dom';
import { GET_DOCTOR_PROFILE } from 'gql/profile/queries';
import { DoctorById } from 'gql/profile/__generated__/DoctorById';
import { useAuthStateContext } from 'providers/Auth';

import { Loading, PageWrapper } from 'components/ui';
import {
  Overview,
  Settings,
  WorkingHours,
  Plans,
  PaymentInfo
} from './components';
import { getFileUrl } from 'utils/file';
import { parseJson } from 'utils/json';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const Profile: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams();
  const lang = useMemo(() => searchParams.get('lang'), [searchParams]);
  const { user: me } = useAuthStateContext();

  const { data: profileData, loading } = useQuery<DoctorById>(
    GET_DOCTOR_PROFILE,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        id: me?.id,
        lang
      }
    }
  );

  const profile = profileData?.doctorById.data;

  useEffect(() => {
    if (!lang) {
      searchParams.set('lang', 'en');
      setSearchParams(searchParams);
    }
  }, [lang, searchParams, setSearchParams]);

  const changeLocale = (val: string) => {
    searchParams.set('lang', val);
    setSearchParams(searchParams);
  };

  if (!lang || !me) return null;

  if (loading) {
    return <Loading />;
  }

  return (
    <PageWrapper
      title={`${me.first_name || ''} ${me.last_name || ''}`}
      extra={[
        <Select
          placeholder={t('doctor_profile.profile_language')}
          size="large"
          value={lang}
          onChange={val => changeLocale(val)}
          key="profile-lang-selector"
        >
          {LANGUAGES.map(item => (
            <Option
              key={`profile-lang-selector-${item.key}`}
              value={item.locale}
            >
              {item.label}
            </Option>
          ))}
        </Select>
      ]}
    >
      <Space size={32} direction="vertical" style={{ width: '100%' }}>
        <Card style={{ backgroundColor: 'transparent' }}>
          {me && (
            <Row justify="space-between">
              <Col>
                <Space size={16} align="start">
                  <Image
                    src={me.avatar ? getFileUrl(me.avatar) : placeholder}
                    width={240}
                    height={138}
                    preview={false}
                    style={{ objectFit: 'cover', borderRadius: 10 }}
                  />
                  <div>
                    <Space>
                      <Title level={4}>
                        {me.first_name} {me.last_name}
                      </Title>
                    </Space>
                    {!!profile?.specialties?.length && (
                      <Paragraph>
                        {profile.specialties.map(item => item.name).join(', ')}
                      </Paragraph>
                    )}
                    {!!profile?.languages?.length && (
                      <Paragraph>
                        {parseJson(profile.languages)
                          ?.map((item: { label: string }) => item?.label)
                          .join(' | ')}
                      </Paragraph>
                    )}
                    {profile?.address && (
                      <Paragraph>{profile.address}</Paragraph>
                    )}
                  </div>
                </Space>
              </Col>
              <Col md={6}>
                <Space direction="vertical" size={50} style={{ width: '100%' }}>
                  <div>
                    {me.phone && (
                      <Paragraph>
                        <Space size={8}>
                          <PhoneOutlined />
                          {me.phone}
                        </Space>
                      </Paragraph>
                    )}
                    <Paragraph>
                      <Space size={8}>
                        <MessageOutlined />
                        {me.email}
                      </Space>
                    </Paragraph>
                  </div>
                </Space>
              </Col>
            </Row>
          )}
        </Card>
        <Card style={{ backgroundColor: 'transparent' }}>
          <Tabs
            defaultActiveKey={id}
            items={[
              {
                label: t('profile.overview'),
                key: 'overview',
                children: <Overview />
              },
              {
                label: t('profile.working_hours'),
                key: 'working-hours',
                children: <WorkingHours />
              },
              {
                label: t('profile.plans'),
                key: 'plans',
                children: <Plans />
              },
              {
                label: t('profile.settings'),
                key: 'settings',
                children: <Settings />
              },
              {
                label: t('profile.payment_info'),
                key: 'payment',
                children: <PaymentInfo />
              }
            ]}
          />
        </Card>
      </Space>
    </PageWrapper>
  );
};

export default Profile;
